import type { ExitStatus } from '@/objects/generated/enums/ExitStatus';
import type MatrixStationRoleCell from './MatrixStationRoleCell';
import type IMatrixConfig from './IMatrixConfig';
import type { CategoryId, GroupId, RaceId, StationRoleId } from '@/utilities/typealiases';
import type { MatrixContestant } from './MatrixContestant';

interface IMatrixRow {
    RaceContestantId: number;
    ContestantId: number;
    Bib: number;
    StartTime: Date | null;
    ExitStatus: ExitStatus;
    ExitStatusFlagTime: Date | null;
    ExitStatusReason: string | null;
    ContestantName: string;
    TeamName: string | null;
    RaceID: RaceId;
    GroupID: GroupId;
    GroupName: string;
    CategoryID: CategoryId;
    CategoryName: string;
    StationRoles: MatrixStationRoleCell[];
    MaxLap: number;
    HasStartGPI: boolean;
    MatrixConfig: IMatrixConfig;
}

type MatrixStationRoleCellWithIndex = { cell: MatrixStationRoleCell; cellIndex: number };

export default class MatrixRow {
    public RaceContestantId: number;
    public ContestantId: number;
    public Bib: number;
    public StartTime: Date | null;
    public ExitStatus: ExitStatus = 'None';
    public ExitStatusFlagTime: Date | null;
    ExitStatusReason: string | null;
    public ContestantName: string;
    public TeamName: string | null;
    public RaceID: RaceId;
    public GroupID: GroupId;
    public GroupName: string;
    public CategoryID: CategoryId;
    public CategoryName: string;
    public StationRoles: MatrixStationRoleCell[];

    /**
     * True if the contestant has a time on a station that is a finish station
     */
    public HasFinishTime: boolean;

    /**
     * True if the contestant has a time on a station that is a start station
     */
    public HasStartPass: boolean;

    /**
     * The maximum number of laps the contestant can do
     */
    public MaxLap: number;
    public MarkedForRemoval: boolean;
    public MarkedAsNew: boolean;

    /**
     * True if the contestant has a GPI reading used for start
     */
    public HasStartGPI: boolean;
    public MatrixConfig: IMatrixConfig;
    public IsNextToStart: boolean;

    public _CellByStationRoleIdAndLapNumber: Map<string, MatrixStationRoleCellWithIndex>;

    constructor(data: IMatrixRow) {
        this.StationRoles = data.StationRoles;
        this.RaceContestantId = data.RaceContestantId;
        this.ContestantId = data.ContestantId;
        this.Bib = data.Bib;
        this.StartTime = data.StartTime;
        this.ExitStatus = data.ExitStatus;
        this.ExitStatusFlagTime = data.ExitStatusFlagTime;
        this.ExitStatusReason = data.ExitStatusReason;
        this.ContestantName = data.ContestantName;
        this.TeamName = data.TeamName;
        this.RaceID = data.RaceID;
        this.GroupID = data.GroupID;
        this.GroupName = data.GroupName;
        this.CategoryID = data.CategoryID;
        this.CategoryName = data.CategoryName;
        this.HasFinishTime = false;
        this.HasStartPass = false;
        this.MaxLap = data.MaxLap;
        this.MarkedForRemoval = false;
        this.MarkedAsNew = false;
        this.HasStartGPI = data.HasStartGPI;
        this.MatrixConfig = data.MatrixConfig;
        this.IsNextToStart = false;

        // Create a lookup map
        this._CellByStationRoleIdAndLapNumber = new Map<string, MatrixStationRoleCellWithIndex>(
            this.StationRoles.map((cell, cellIndex) => ({ cell, cellIndex }))
                .filter((x) => !x.cell.IsFillerCell)
                .map((x) => [`${x.cell.StationRoleId}-${x.cell.LapNumber}`, { cell: x.cell, cellIndex: x.cellIndex }]),
        );
    }

    public UpdatedByMatrixContestant(mc: MatrixContestant) {
        this.Bib = mc.Bib || 0;
        this.ExitStatus = mc.ExitStatus;
        this.ExitStatusFlagTime = mc.ExitStatusFlagTime;
        this.ExitStatusReason = mc.ExitStatusReason;
        this.StartTime = mc.Starttime;
        this.ContestantName = mc.ContestantName || '';
        this.TeamName = mc.TeamName;
        this.RaceID = mc.RaceID;
        this.GroupID = mc.GroupID;
        this.GroupName = mc.GroupName!;
        this.CategoryID = mc.CategoryID;
        this.CategoryName = mc.CategoryName!;
    }

    /**
     *
     * @param stationRoleId The stationRole we want to find the cell for
     * @returns
     */
    public GetStationRoleCellByStationRoleAndLapNumber(stationRoleId: StationRoleId, lapNumber: number): MatrixStationRoleCellWithIndex {
        const cellWithIndex = this._CellByStationRoleIdAndLapNumber.get(`${stationRoleId}-${lapNumber}`);
        if (!cellWithIndex) {
            throw new Error(`Could not find cell for StationRoleId ${stationRoleId} and LapNumber ${lapNumber}`);
        }
        return cellWithIndex;
    }
}
